import React from "react";
import InstalledExtensionsTable from "../components/InstalledExtensions/InstalledExtensionsTable";
import AvailableExtensionsPanel from "../components/ExtensionInstallation/AvailableExtensionsPanel";
import {IExtension, IInstalledExtension} from "../models/Extension";
import {CommandBar, DefaultButton, Dialog, DialogFooter, ICommandBarItemProps} from "@fluentui/react";
import {
    useDeleteInstalledExtensionMutation,
    useDisableInstalledExtensionMutation,
    useEnableInstalledExtensionMutation, 
    useGetInstalledExtensionsQuery
} from "../store/Api";
import useCan, {ORG_EXTENSION_MODIFY} from "../permissions/ability";
import ExtensionDetailsPanel from "../components/ExtensionInstallation/ExtensionDetailsPanel";
import ExtensionFolioDetailsPanel from "../components/ExtensionFolios/ExtensionFolioDetailsPanel";
import FeatureRequestPanel from "../components/FeatureRequestPanel/FeatureRequestPanel";
import CrmSetupDialog, { CrmSetupMode } from "../components/GettingStarted/CrmSetupDialog";
import DistributorSetupDialog from "../components/GettingStarted/DistributorSetupDialog";
import { useHistory, useLocation } from "react-router-dom";
import {useUrlPanelState} from "../hooks/useUrlState";

/** The Getting Started checklist deep-links the CRM step here with this query param. */
const CRM_SETUP_PARAM = 'crmSetup';
/** ...and the follow-on "configure your integration" step with this one, which opens the same
 * dialog past the connecting half. */
const CRM_CONFIGURE_PARAM = 'crmConfigure';
/** ...and the distributor step here, which opens the distributor picker. */
const DISTRIBUTOR_SETUP_PARAM = 'distributorSetup';
/** ...and the picker names a folio here when the operator chooses one to connect. */
const INSTALL_FOLIO_PARAM = 'installFolio';

const ExtensionManagement: React.FC = () => {
    const [isNew, setIsNew] = React.useState(false);
    // Installing a new extension is a transient flow with nothing to link to, so it stays local.
    const [newPanelOpen, setNewPanelOpen] = React.useState(false);
    const [newFolioPanelOpen, setNewFolioPanelOpen] = React.useState(false);
    // Opening an already-installed extension goes through the URL, which is what makes an
    // extension's configuration fields reachable by a documentation link.
    const extensionPanel = useUrlPanelState('extension');
    const folioPanel = useUrlPanelState('extensionFolio');
    const [availablePanelOpen, setAvailablePanelOpen] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState<IInstalledExtension | undefined>(undefined);
    const [selectedExtension, setSelectedExtension] = React.useState<IExtension | undefined>();
    const [disableExtension] = useDisableInstalledExtensionMutation();
    const [enableExtension] = useEnableInstalledExtensionMutation();
    const [deleteExtension] = useDeleteInstalledExtensionMutation();
    const getInstalled = useGetInstalledExtensionsQuery();
    const [frOpen, setFrOpen] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [crmSetupOpen, setCrmSetupOpen] = React.useState(false);
    const [distributorPickerOpen, setDistributorPickerOpen] = React.useState(false);
    const [distributorCandidate, setDistributorCandidate] = React.useState<string | undefined>();
    // Folio id the distributor picker asked us to install. Only an id is needed: the folio panel
    // resolves the definition itself, so there is no available-extension record to carry around.
    const [installFolioId, setInstallFolioId] = React.useState<string | undefined>();
    const history = useHistory();
    const location = useLocation();

    // ?crmSetup=1 opens the guided CRM setup dialog; any other value names a CRM to open straight
    // onto (edit/re-run, skipping the picker). The param is stripped once it has been read:
    // leaving it in the URL would reopen the dialog every time the user closed it, and would
    // resurrect it on a refresh long after the CRM was connected.
    const [crmSetupCandidate, setCrmSetupCandidate] = React.useState<string | undefined>();
    const [crmSetupMode, setCrmSetupMode] = React.useState<CrmSetupMode>('connect');
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const setup = params.get(CRM_SETUP_PARAM);
        const configure = params.get(CRM_CONFIGURE_PARAM);
        const distributor = params.get(DISTRIBUTOR_SETUP_PARAM);
        const installFolio = params.get(INSTALL_FOLIO_PARAM);
        const value = setup ?? configure;
        if (!value && !distributor && !installFolio) return;
        if (value) {
            setCrmSetupMode(configure ? 'configure' : 'connect');
            setCrmSetupCandidate(value === '1' ? undefined : value);
            setCrmSetupOpen(true);
        }
        // The distributor step opens the picker rather than the whole install catalog: one tile per
        // distributor, not one row per capability. Same strip-the-param rule as the CRM params -- leaving
        // it in would reopen the dialog on every close and resurrect it on a refresh.
        if (distributor) {
            // Any value other than "1" names the distributor to open straight onto, which is how the
            // checklist sends someone back into an unfinished setup instead of to the picker.
            setDistributorCandidate(distributor === '1' ? undefined : distributor);
            setDistributorPickerOpen(true);
        }
        // ?installFolio=<id> is how the picker starts a connection it found nothing installed for.
        if (installFolio) {
            setInstallFolioId(installFolio);
            setNewFolioPanelOpen(true);
            setIsNew(true);
        }
        params.delete(CRM_SETUP_PARAM);
        params.delete(CRM_CONFIGURE_PARAM);
        params.delete(DISTRIBUTOR_SETUP_PARAM);
        params.delete(INSTALL_FOLIO_PARAM);
        const remaining = params.toString();
        history.replace(`${location.pathname}${remaining ? `?${remaining}` : ''}`);
    }, [location.search, location.pathname, history]);

    // Resolved from the URL rather than held in state, so a link opens the right extension even on
    // a cold load, once the installed list has arrived.
    const detailsExtension = React.useMemo(
        () => getInstalled.data?.find(e => e.id === (extensionPanel.instanceId ?? folioPanel.instanceId)),
        [getInstalled.data, extensionPanel.instanceId, folioPanel.instanceId]
    );

    /*
     * Which panel opens follows the record's own type, not which param the link used. A folio opened
     * as ?panel=extension used to mount the non-folio panel, whose available-extension query is
     * skipped for folios — so it rendered "An error occurred loading the extension" while the folio
     * panel mounted alongside it and threw on data it never fetched. Link authors should not have to
     * know which of the two keys an extension needs.
     */
    const detailsIsFolio = detailsExtension?.extensionType === 'Folio';
    const detailsRequested = (extensionPanel.isOpen || folioPanel.isOpen) && !!detailsExtension;

    const openDetails = async (installed: IInstalledExtension) => {
        if(installed.extensionType == 'Folio'){
            folioPanel.open(installed.id);
        }
        else{
            extensionPanel.open(installed.id);
        }
    }

    const closeDetails = async () => {
        setSelectedExtension(undefined);
        setInstallFolioId(undefined);
        setNewPanelOpen(false);
        setNewFolioPanelOpen(false);
        extensionPanel.close();
        folioPanel.close();
        setIsNew(false);
        await refetch();
    }

    const toggleInstalled = async (installed: IInstalledExtension) => {
        if (installed.enabled) {
            await disableExtension(installed.id);
        } else {
            await enableExtension(installed.id);
        }
    };

    const confirmDeleteInstalled = async (installed: IInstalledExtension) => {
        await deleteExtension(installed.id);
    };

    const commands: ICommandBarItemProps[] = [
        {
            key: 'install',
            text: 'Install',
            iconProps: { iconName: 'Add' },
            onClick: () => {
                setAvailablePanelOpen(true);
            }
        }
    ]

    const farCommands: ICommandBarItemProps[] = [
        {
            key: 'refresh',
            text: 'Refresh',
            iconOnly: true,
            iconProps: { iconName: 'Refresh' },
            onClick: () => {
                getInstalled.refetch();
            }
        },
    ]
    
    const refetch = async() => {
        setIsSaving(true);
        await new Promise(f => setTimeout(f, 500));
        getInstalled.refetch();
        setIsSaving(false);
    }

    const canModify = useCan(ORG_EXTENSION_MODIFY);

    return (
        canModify ?
        <div className="main-content">
            <h1>Extension Management</h1>
            <CommandBar items={commands} farItems={farCommands} />
            <InstalledExtensionsTable
                onDelete={(extension) => setConfirmDelete(extension)}
                installedExtensions={getInstalled.data?.filter(a => !a.isSubExtension) ?? []}
                isLoading={isSaving || getInstalled.isFetching || getInstalled.isLoading}
                onAvailableOpen={() => setAvailablePanelOpen(true)}
                onDetailsSelect={(e) => openDetails(e)}
                onDisableToggle={(e) => toggleInstalled(e)}
            />
            <ExtensionDetailsPanel
                installedExtension={detailsExtension}
                extensionId={selectedExtension?.id ?? ""}
                isOpen={newPanelOpen || (detailsRequested && !detailsIsFolio)}
                onClose={closeDetails}
                isNew={isNew}
            />
            <ExtensionFolioDetailsPanel
                isOpen={newFolioPanelOpen || (detailsRequested && detailsIsFolio)}
                onClose={closeDetails}
                installedExtension={detailsExtension}
                extensionId={selectedExtension?.id ?? installFolioId ?? ""}
                isNew={false}/>
            <AvailableExtensionsPanel
                isOpen={availablePanelOpen}
                onClose={() => {
                    setAvailablePanelOpen(false);
                    setSelectedExtension(undefined);
                }}
                onSelect={(e) => {
                    setAvailablePanelOpen(false);
                    if(e.category == "folio") {
                        setNewFolioPanelOpen(true);
                    }
                    else{
                        setNewPanelOpen(true);
                    }
                    setSelectedExtension(e);
                    setIsNew(true);
                }}
                onFeatureRequest={() => {
                    setAvailablePanelOpen(false);
                    setFrOpen(true)
                }}
            />
            <Dialog
                hidden={confirmDelete == undefined}
                modalProps={{
                    isBlocking: true,
                    onDismiss:() => {
                        setConfirmDelete(undefined)
                    }
                }}
                dialogContentProps={{
                    title: 'Confirm Uninstall',
                    subText: `Are you sure you want to uninstall ${confirmDelete?.nickname ?? confirmDelete?.name}? This action cannot be undone.`,
                }}
                >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Confirm'
                        onClick={() => {
                            if (confirmDelete)
                                confirmDeleteInstalled(confirmDelete).then(() => { setConfirmDelete(undefined) });
                        }}
                        />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => {
                            setConfirmDelete(undefined)
                        }}
                        />
                </DialogFooter>
            </Dialog>
            <DistributorSetupDialog
                isOpen={distributorPickerOpen}
                initialCandidateId={distributorCandidate}
                onDismiss={() => {
                    setDistributorPickerOpen(false);
                    setDistributorCandidate(undefined);
                }}
            />
            <CrmSetupDialog
                isOpen={crmSetupOpen}
                initialCandidateId={crmSetupCandidate}
                mode={crmSetupMode}
                onOpenFolioSettings={(installedId) => folioPanel.open(installedId)}
                onDismiss={() => {
                    setCrmSetupOpen(false);
                    setCrmSetupCandidate(undefined);
                    setCrmSetupMode('connect');
                }}
            />
            <FeatureRequestPanel
                isOpen={frOpen}
                onClose={() => setFrOpen(false)}
                description="Don't see the extension you need? Request a new one! We are always looking to expand our offerings and user feedback plays a big role in that."
                tags={['Extensions']}
                title='Request New Extension'
                />
        </div>
         : <></>
    );
}

export default ExtensionManagement;
